import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { adminService } from '@/shared/services/adminService'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'
import { useAuthStore } from '@/shared/stores/authStore'
import { WorkspaceRole } from '@/shared/types/entities'

interface RoleCheckResult {
  isOwner: boolean
  isAdmin: boolean
  isMember: boolean
  isTimeLogger: boolean
  isViewer: boolean
  isRestricted: boolean
  role: WorkspaceRole
  isLoading: boolean
  isError: boolean
  canManageWorkspace: boolean
  canManageMembers: boolean
  canViewReports: boolean
  canTrackTime: boolean
}

const ROLE_HIERARCHY: Record<WorkspaceRole, number> = {
  [WorkspaceRole.OWNER]: 4,
  [WorkspaceRole.ADMIN]: 3,
  [WorkspaceRole.MEMBER]: 2,
  [WorkspaceRole.TIME_LOGGER]: 1,
  [WorkspaceRole.VIEWER]: 0,
}

function hasMinimumRole(userRole: WorkspaceRole, requiredRole: WorkspaceRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole]
}

export function useRoleCheck(): RoleCheckResult {
  const { selectedWorkspace } = useWorkspaceStore()
  const { user } = useAuthStore()
  const workspaceId = selectedWorkspace?.id
  const userId = user?.id

  const { data: members, isLoading, isError, error } = useQuery({
    queryKey: ['workspace-members-for-role', workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error('No workspace selected')
      return adminService.getWorkspaceMembers(workspaceId)
    },
    enabled: !!workspaceId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  // Log error for debugging
  if (isError && error) {
    console.error('useRoleCheck: Failed to fetch members:', error)
  }

  return useMemo(() => {
    if (!workspaceId || !userId || isLoading || isError || !Array.isArray(members)) {
      return {
        isOwner: false,
        isAdmin: false,
        isMember: false,
        isTimeLogger: false,
        isViewer: true,
        isRestricted: true,
        role: WorkspaceRole.VIEWER,
        isLoading,
        isError,
        canManageWorkspace: false,
        canManageMembers: false,
        canViewReports: false,
        canTrackTime: false,
      }
    }

    const currentMember = members.find(m => m.userId === userId)
    const userRole = (currentMember?.role as WorkspaceRole) ?? WorkspaceRole.VIEWER

    return {
      isOwner: userRole === WorkspaceRole.OWNER,
      isAdmin: hasMinimumRole(userRole, WorkspaceRole.ADMIN),
      isMember: hasMinimumRole(userRole, WorkspaceRole.MEMBER),
      isTimeLogger: userRole === WorkspaceRole.TIME_LOGGER,
      isViewer: userRole === WorkspaceRole.VIEWER,
      isRestricted: !hasMinimumRole(userRole, WorkspaceRole.MEMBER),
      role: userRole,
      isLoading: false,
      isError: false,
      canManageWorkspace: hasMinimumRole(userRole, WorkspaceRole.ADMIN),
      canManageMembers: hasMinimumRole(userRole, WorkspaceRole.ADMIN),
      canViewReports: hasMinimumRole(userRole, WorkspaceRole.MEMBER),
      canTrackTime: hasMinimumRole(userRole, WorkspaceRole.TIME_LOGGER),
    }
  }, [members, userId, workspaceId, isLoading, isError])
}

export function useWorkspaceUsageStats() {
  const { selectedWorkspace } = useWorkspaceStore()
  const workspaceId = selectedWorkspace?.id

  return useQuery({
    queryKey: ['workspace-usage-stats', workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error('No workspace selected')
      return adminService.getWorkspaceUsageStats(workspaceId)
    },
    enabled: !!workspaceId,
  })
}
